import { getCookie } from '../../../utils/helpers';
import {
  handle401Error,
  handle403Error
} from '../../../utils/authErrorHandler';

// Form field configurations for different moment types
const getFormTemplate = (itemType: string) => {
  const isKeyDate = itemType === 'Key Date' || itemType === 'KeyDate';

  return {
    sections: [
      {
        columns: 1,
        heading: '',
        subheading: '',
        fields: [
          {
            id: 'momentType',
            name: 'momentType',
            label: 'TYPE OF RELEASE OR MOMENT',
            type: 'select',
            required: true,
            options: [
              { value: 'Event', label: 'Event' },
              { value: 'Key Date', label: 'Key Date' }
            ],
            defaultValue: isKeyDate ? 'Key Date' : 'Event'
          },
          {
            id: 'name',
            name: 'name',
            label: 'NAME',
            type: 'text',
            required: true,
            placeholder: `Enter ${isKeyDate ? 'key date' : 'event'} name`
          },
          {
            id: 'scheduleRange',
            name: 'scheduleRange',
            label: 'DATE OR DATE RANGE',
            type: 'daterangepicker',
            required: true
          },
          {
            id: 'description',
            name: 'description',
            label: 'DESCRIPTION',
            type: 'textarea',
            required: false,
            placeholder: 'Enter description (optional)'
          }
        ]
      }
    ]
  };
};

/**
 * Fetch edit event template configuration
 * Returns form template without external API dependencies
 */
export async function fetchEditEventTemplate(
  itemType: string = 'Event'
): Promise<Record<string, any>> {
  // Normalize item type
  const normalizedItemType = itemType === 'KeyDate' ? 'Key Date' : itemType;
  const templateKey = normalizedItemType === 'Key Date' ? 'Key Date' : 'Event';

  try {
    // Get template configuration
    const template = getFormTemplate(templateKey);

    return {
      [templateKey]: template
    };
  } catch (error) {
    console.error('Error generating edit template:', error);
    throw new Error(
      `Failed to generate template for ${templateKey}: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    );
  }
}

/**
 * Configuration constants
 */
const AUTH_TOKEN_COOKIE = 'app_access_token';
const DEFAULT_API_BASE_URL =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:8080/product-roadmap/api/v2'
    : '/product-roadmap/api/v2'; // Use relative path for production

/**
 * Get authentication headers for API requests
 */
const getAuthHeaders = () => {
  const token = getCookie(AUTH_TOKEN_COOKIE);
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

/**
 * Get API base URL from environment or use default
 */
const getApiBaseUrl = (): string => {
  return process.env.REACT_APP_API_V2_URL || DEFAULT_API_BASE_URL;
};

/**
 * Update moment fields by issueId using PATCH API
 */
export async function updateMoment(
  issueId: string,
  updateFields: Record<string, any>
): Promise<any> {
  if (!issueId) {
    throw new Error('Issue ID is required');
  }

  if (!updateFields || Object.keys(updateFields).length === 0) {
    throw new Error('Update fields are required');
  }

  try {
    const headers = getAuthHeaders();

    const apiBaseUrl = getApiBaseUrl();
    const response = await fetch(`${apiBaseUrl}/moments/${issueId}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(updateFields)
    });

    if (!response.ok) {
      let errorMessage = `Failed to update moment: ${response.status}`;

      // Handle authentication errors with centralized handlers
      if (response.status === 401) {
        handle401Error('Updating moment data');
        throw new Error('Authentication required');
      }

      if (response.status === 403) {
        handle403Error('Updating moment data');
        throw new Error('Permission denied');
      }

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (parseError) {
        // If we can't parse the error response, use the default message
      }

      throw new Error(errorMessage);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error updating moment:', error);
    throw error;
  }
}

/**
 * Update moment via card PATCH endpoint
 * PATCH /moments/{issueId}/card
 * Use for dates (PCO typed objects), riskLevel, name, headline
 */
export async function updateMomentCard(
  issueId: string,
  payload: {
    momentType: string;
    releasePhaseId?: string;
    fields: Record<string, any>;
  }
): Promise<any> {
  if (!issueId) throw new Error('Issue ID is required');

  const headers = getAuthHeaders();
  const apiBaseUrl = getApiBaseUrl();

  const response = await fetch(`${apiBaseUrl}/moments/${issueId}/card`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload)
  });

  if (response.status === 401) {
    handle401Error('Updating moment card');
    throw new Error('Authentication required');
  }
  if (response.status === 403) {
    handle403Error('Updating moment card');
    throw new Error('Permission denied');
  }
  if (!response.ok) {
    let errorMessage = `Failed to update moment card: ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      // ignore parse error
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * Add a new RTM phase to an existing moment
 * POST /moments/{issueId}/rtm-phases
 */
export async function addRtmPhase(
  issueId: string,
  routeToMarket: string
): Promise<any> {
  if (!issueId) throw new Error('Issue ID is required');
  if (!routeToMarket) throw new Error('routeToMarket is required');

  const headers = getAuthHeaders();
  const apiBaseUrl = getApiBaseUrl();

  const response = await fetch(`${apiBaseUrl}/moments/${issueId}/rtm-phases`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ routeToMarket })
  });

  if (response.status === 401) {
    handle401Error();
    throw new Error('Unauthorized');
  }
  if (response.status === 403) {
    handle403Error();
    throw new Error('Forbidden');
  }
  if (!response.ok) {
    let errorMessage = `Failed to add RTM phase: ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      // ignore parse error
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * Update the routeToMarket of an existing RTM phase
 * PATCH /moments/{issueId}/rtm-phases/{phaseId}
 */
export async function updateRtmPhase(
  issueId: string,
  phaseId: string,
  routeToMarket: string
): Promise<any> {
  return patchRtmPhase(issueId, phaseId, { routeToMarket });
}

/**
 * Patch any fields on an existing RTM phase
 * PATCH /moments/{issueId}/rtm-phases/{phaseId}
 */
export async function patchRtmPhase(
  issueId: string,
  phaseId: string,
  payload: Record<string, any>
): Promise<any> {
  if (!issueId) throw new Error('Issue ID is required');
  if (!phaseId) throw new Error('Phase ID is required');

  const headers = getAuthHeaders();
  const apiBaseUrl = getApiBaseUrl();

  const response = await fetch(
    `${apiBaseUrl}/moments/${issueId}/rtm-phases/${phaseId}`,
    {
      method: 'PATCH',
      headers,
      body: JSON.stringify(payload)
    }
  );

  if (response.status === 401) {
    handle401Error();
    throw new Error('Unauthorized');
  }
  if (response.status === 403) {
    handle403Error();
    throw new Error('Forbidden');
  }
  if (!response.ok) {
    let errorMessage = `Failed to update RTM phase: ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      // ignore parse error
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

// JIRA validation api
export const getJiraLinkValidation = async (jiraId: string): Promise<any> => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    const headers = getAuthHeaders();
    const response = await fetch(`${apiBaseUrl}/jira/${jiraId}`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      let errorMessage = `Failed to validate jira link with jiraId: ${jiraId}`;

      // Handle authentication errors with centralized handlers
      if (response.status === 401) {
        handle401Error('Validating JIRA link');
        throw new Error('Authentication required');
      }

      if (response.status === 403) {
        handle403Error('Validating JIRA link');
        throw new Error('Permission denied');
      }

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (error) {
        // if parse error use the default message
      }
      throw new Error(errorMessage);
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error validating jira link:', error);
    throw error;
  }
};
